import React from 'react'
import { useRouter } from 'next/router'
import cx from 'classnames'
import styles from './SubMenu.module.scss'
import { Heading } from '~elements'
import { SubMenuFeature } from '../SubMenuFeature/SubMenuFeature'
import { Link, NormalizedSubNavItem, RawSubNavItem, SubMenuProps } from '../../types'
import { useNavigation } from '../../NavigationContext'

/**
 * Converts raw submenu items into a consistent normalized format.
 *
 * Each item may contain top-level links or nested `sectionLinks`.
 * This function flattens them into a unified `links` array and
 * normalizes optional fields (`isTargetBlank`, `hasDivider`, etc.)
 * so the navigation component can render them predictably
 * and render valid ul li markup.
 */
function normalizeSubNavItems(items: RawSubNavItem[]): NormalizedSubNavItem[] {
  return items.map(item => {
    const links: Link[] = []

    // sectionLinks (inside SubmenuSection)
    if (item.sectionLinks && item.sectionLinks.length > 0) {
      links.push(
        ...item.sectionLinks.map(link => ({
          title: link.title,
          url: link.url,
          isTargetBlank: link.isTargetBlank ?? false,
          hasDivider: link.hasDivider ?? null,
          hasAccentDivider: link.hasAccentDivider ?? null
        }))
      )
    }

    // top-level NavigationMenuItem
    if (item.title && item.url) {
      links.push({
        title: item.title,
        url: item.url,
        isTargetBlank: item.isTargetBlank ?? false,
        hasDivider: item.hasDivider ?? null,
        hasAccentDivider: item.hasAccentDivider ?? null
      })
    }

    return {
      groupTitle: item.groupTitle,
      hasDivider: item.hasDivider ?? null,
      hasAccentDivider: item.hasAccentDivider ?? null,
      links,
      isMobileView: item.isMobileView
    }
  })
}

export const SubMenu = ({ parentIndex, item }: SubMenuProps) => {
  const { asPath } = useRouter()
  const {
    submenuImage: featureImage,
    submenuRedirectUrl: featureUrl,
    submenuImageSubtitle: featureSubtitle,
    submenuImageTitle: featureTitle
  } = item || {}
  const { fireAnalyticsClickEvent, toggleNavDrawer, isMobileView, setOpenSubMenuIndex, openSubMenuIndex } =
    useNavigation()

  const normalizedItems = normalizeSubNavItems(item.subNavLinks || [])

  return (
    <div
      className={cx([
        styles['submenu'],
        openSubMenuIndex == parentIndex && styles['submenu--is-open'],
        styles[`submenu--${isMobileView ? 'mobile' : 'desktop'}`]
      ])}
    >
      {/* Column one: all links */}
      <ul className={styles['submenu__list']}>
        {normalizedItems.map(({ groupTitle, links }, index) => (
          <li key={groupTitle ?? links[0].url} className={styles['submenu__section']}>
            {groupTitle && (
              <div className={styles['submenu__list-header']}>
                <Heading level={4} noMarginBottom>
                  {groupTitle}
                </Heading>
              </div>
            )}
            <ul className={styles['submenu__section-list']}>
              {links.map(link => {
                const isActive = asPath === link.url
                return (
                  <li
                    key={link.url}
                    className={cx([
                      styles['submenu-item'],
                      link.hasAccentDivider && styles['submenu-item--has-accent-divider']
                    ])}
                  >
                    <a
                      href={link.url}
                      target={link.isTargetBlank ? '_blank' : '_self'}
                      rel={link.isTargetBlank ? 'noopener noreferrer' : undefined}
                      className={isActive ? styles['is-active'] : undefined}
                      onClick={e => {
                        if (isActive) {
                          e.preventDefault()
                          return
                        }

                        if (isMobileView) {
                          toggleNavDrawer()
                        } else {
                          setOpenSubMenuIndex(null)
                        }

                        fireAnalyticsClickEvent(link.url, link.title, `${parentIndex + 1}:${index + 1}`)
                      }}
                    >
                      {link.title}
                    </a>
                  </li>
                )
              })}
            </ul>
          </li>
        ))}
      </ul>

      {/* Column two: Desktop only feature section */}
      {!isMobileView && featureUrl && featureImage && featureTitle && (
        <SubMenuFeature url={featureUrl} image={featureImage} title={featureTitle} subtitle={featureSubtitle} />
      )}
    </div>
  )
}
